import React, { Component } from 'react';

class Analytics extends Component {
    state = {
        analytics: null,
        loading: true,
        error: null,
        selectedPeriod: '30'
    };

    componentDidMount() {
        this.fetchAnalytics();
    }

    fetchAnalytics = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/account/analytics`;
            
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Cache': 'no-cache'
                }
            });
            
            const result = await response.json();
            
            if (result.msg === 'Analytics data') {
                this.setState({ 
                    analytics: result.analytics,
                    loading: false 
                });
            } else {
                this.setState({ 
                    error: 'Failed to load analytics',
                    loading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                error: 'Error fetching analytics',
                loading: false 
            });
        }
    };

    formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    calculatePercentage = (value, total) => {
        if (total === 0) return 0;
        return ((value / total) * 100).toFixed(1);
    };

    getCategoryColor = (category) => {
        const colors = {
            'Food & Dining': '#EF4444',
            'Shopping': '#F59E0B',
            'Transportation': '#3B82F6',
            'Entertainment': '#8B5CF6',
            'Bills & Utilities': '#6366F1',
            'Healthcare': '#EC4899',
            'Transfer': '#14B8A6',
            'Income': '#10B981',
            'Other': '#6B7280'
        };
        return colors[category] || '#6B7280';
    };

    render() {
        const { analytics, loading, error } = this.state;

        if (loading) {
            return (
                <section className="analytics">
                    <h2>Spending Analytics</h2>
                    <div className="loading">Loading analytics...</div>
                </section>
            );
        }

        if (error) {
            return (
                <section className="analytics">
                    <h2>Spending Analytics</h2>
                    <div className="error-message">{error}</div>
                </section>
            );
        }

        if (!analytics) {
            return (
                <section className="analytics">
                    <h2>Spending Analytics</h2>
                    <div className="no-data">No analytics data available</div>
                </section>
            );
        }

        const { 
            total_income, 
            total_expenses, 
            net_savings, 
            spending_by_category,
            transaction_count 
        } = analytics;

        return (
            <section className="analytics">
                <h2>Spending Analytics</h2>
                
                {/* Summary Cards */}
                <div className="analytics-summary">
                    <div className="summary-card income">
                        <h4>Total Income</h4>
                        <span className="amount positive">
                            {this.formatCurrency(total_income)}
                        </span>
                    </div>
                    
                    <div className="summary-card expenses">
                        <h4>Total Expenses</h4>
                        <span className="amount negative">
                            {this.formatCurrency(total_expenses)}
                        </span>
                    </div>
                    
                    <div className="summary-card savings">
                        <h4>Net Savings</h4>
                        <span className={`amount ${net_savings >= 0 ? 'positive' : 'negative'}`}>
                            {this.formatCurrency(net_savings)}
                        </span>
                    </div>
                    
                    <div className="summary-card transactions">
                        <h4>Transactions</h4>
                        <span className="amount">{transaction_count}</span>
                    </div>
                </div>

                {/* Spending by Category */}
                <div className="category-breakdown">
                    <h3>Spending by Category</h3>
                    
                    {Object.keys(spending_by_category).length === 0 ? (
                        <p className="no-data">No spending data available</p>
                    ) : (
                        <div className="category-list">
                            {Object.entries(spending_by_category)
                                .sort((a, b) => b[1] - a[1])
                                .map(([category, amount]) => (
                                    <div key={category} className="category-item">
                                        <div className="category-header">
                                            <span 
                                                className="category-color"
                                                style={{ backgroundColor: this.getCategoryColor(category) }}
                                            ></span>
                                            <span className="category-name">{category}</span>
                                            <span className="category-amount">
                                                {this.formatCurrency(amount)}
                                            </span>
                                        </div>
                                        <div className="category-bar">
                                            <div 
                                                className="category-bar-fill"
                                                style={{ 
                                                    width: `${this.calculatePercentage(amount, total_expenses)}%`,
                                                    backgroundColor: this.getCategoryColor(category)
                                                }}
                                            ></div>
                                        </div>
                                        <span className="category-percentage">
                                            {this.calculatePercentage(amount, total_expenses)}%
                                        </span>
                                    </div>
                                ))
                            }
                        </div>
                    )}
                </div>

                {/* Monthly Trend */}
                {analytics.monthly_trend && Object.keys(analytics.monthly_trend).length > 0 && (
                    <div className="monthly-trend">
                        <h3>Monthly Overview</h3>
                        <div className="trend-table">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th>Income</th>
                                        <th>Expenses</th>
                                        <th>Net</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(analytics.monthly_trend)
                                        .sort((a, b) => b[0].localeCompare(a[0]))
                                        .slice(0, 6)
                                        .map(([month, data]) => (
                                            <tr key={month}>
                                                <td>{month}</td>
                                                <td className="positive">
                                                    {this.formatCurrency(data.income)}
                                                </td>
                                                <td className="negative">
                                                    {this.formatCurrency(data.expenses)}
                                                </td>
                                                <td className={data.income - data.expenses >= 0 ? 'positive' : 'negative'}>
                                                    {this.formatCurrency(data.income - data.expenses)}
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>
        );
    }
}

export default Analytics;

